import { useAppConfig } from "../lib/AppConfigContext";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, 
  Vote, 
  Key, 
  LogOut,
  Menu,
  X,
  Building2,
  Settings,
  Shield,
  UserPlus
} from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import { LanguageSelector } from "./LanguageSelector";
import { LegalFooter } from "./LegalFooter";
import { API_URL } from "../lib/config";

export const Layout = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const entityName = localStorage.getItem("entity_name") || t('nav.entity');
  const entityLogo = localStorage.getItem("entity_logo");

  const handleLogout = async () => {
    try {
      const api = (await import("../lib/api")).default;
      await api.post("/auth/logout").catch(() => {});
    } catch (_) {}
    localStorage.removeItem("entity_id");
    localStorage.removeItem("entity_name");
    localStorage.removeItem("entity_logo");
    localStorage.removeItem("entity_language");
    localStorage.removeItem("is_demo");
    navigate("/login");
  };

  const isDemo = localStorage.getItem("is_demo") === "true";

  const { appName } = useAppConfig();
  
  const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: "/consultes", icon: Vote, label: t('nav.consultes') },
    { path: "/escrutinis", icon: Shield, label: t('entityScrutiny.title') },
    // { path: "/keys", icon: Key, label: t('nav.keys') },
    { path: "/registre", icon: UserPlus, label: t('registerVoters.title') },
    { path: "/settings", icon: Settings, label: t('nav.settings') },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Demo banner */}
      {isDemo && (
        <div className="bg-amber-500 text-white text-center py-2 px-4 text-sm font-medium sticky top-0 z-[60]" data-testid="demo-banner">
          {t('demo.demoBanner')}
        </div>
      )}
      {/* Top bar with language selector */}
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-3 lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <span className="font-heading font-bold text-slate-900">{appName}</span>
          </div>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 text-sm text-slate-600">
              <Building2 className="h-4 w-4" />
              <span>{entityName}</span>
            </div>
            <LanguageSelector />
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky top-12 left-0 z-30 h-[calc(100vh-48px)] w-64 bg-white border-r border-slate-200
            transform transition-transform duration-200 ease-in-out
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          `}
        >
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="hidden lg:flex items-center gap-3 px-6 py-5 border-b border-slate-200">
              <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                <Vote className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-slate-900">{appName}</h1>
                <p className="text-xs text-slate-500">{t('consulta.title')}</p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-3 py-4 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    nav-link
                    ${isActive(item.path) ? "active" : ""}
                  `}
                  data-testid={`nav-${item.path.replace('/', '')}`}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>

            {/* User section */}
            <div className="border-t border-slate-200 p-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center overflow-hidden">
                  {entityLogo ? (
                    <img 
                      src={`${API_URL}${entityLogo}`} 
                      alt={entityName}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Building2 className="h-5 w-5 text-slate-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{entityName}</p>
                  <p className="text-xs text-slate-500">{t('nav.entity')}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                {t('nav.logout')}
              </Button>
            </div>
          </div>
        </aside>

        {/* Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/20 z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 min-h-0 flex flex-col">
          <div className="flex-1">
            {children}
          </div>
          <LegalFooter />
        </main>
      </div>
    </div>
  );
};

export default Layout;
